/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import OpenInFullIcon from '@mui/icons-material/OpenInFullRounded';
import { Box, IconButton, Link, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import katex from 'katex';
import Markdown from 'markdown-to-jsx';
import {
  type ComponentProps,
  type ComponentType,
  memo,
  type ReactNode,
  useMemo,
  useState,
} from 'react';
import MarkdownArtifactAction from './MarkdownArtifactAction';
import MarkdownCodeBlock from './MarkdownCodeBlock';
import MarkdownGuardedScrollBox from './MarkdownGuardedScrollBox';
import {
  encodeMathAsTags,
  escapeIntrawordUnderscores,
  isLatexCodeLanguage,
  isMarkdownCodeLanguage,
  MATH_TAG,
  type MarkdownPipeTableSource,
  removeAdjacentDuplicateMath,
  splitTextIntoMarkdownSegments,
  splitTextIntoSegments,
  stableStringHash,
} from './markdownContentUtils';
import 'katex/dist/katex.min.css';

export interface MarkdownTableExpansion {
  content: ReactNode;
  title: string;
  /** The parsed pipe table, when it is one; lets a workspace rebuild it after a reload. */
  source?: MarkdownPipeTableSource;
}

export interface MarkdownPreviewExpansion {
  content: string;
  title: string;
}

export interface MarkdownCodeExpansion {
  content: string;
  language: string;
  title: string;
}

export type MarkdownLinkComponent = ComponentType<ComponentProps<'a'>>;

// Static sx objects hoisted to module scope so emotion reuses its serialized-CSS
// cache across the thousands of nodes a dense message renders, instead of
// re-serializing a fresh object literal for every node on every render.
const TEXT_SEGMENT_SX = {
  fontSize: 14,
  overflowX: 'hidden',
  '& p': { mb: 1, mt: 0 },
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { mb: 1, mt: 0, pl: 3 },
} as const;

interface MarkdownContentProps {
  text: string;
  isGeneratingMessage?: boolean;
  error?: boolean;
  onExpandCode?: (code: MarkdownCodeExpansion) => void;
  onExpandTable?: (table: MarkdownTableExpansion) => void;
  onExpandMarkdown?: (markdown: MarkdownPreviewExpansion) => void;
  renderLink?: MarkdownLinkComponent;
}

function MarkdownContent({
  text,
  isGeneratingMessage = false,
  error = false,
  onExpandCode,
  onExpandTable,
  onExpandMarkdown,
  renderLink,
}: Readonly<MarkdownContentProps>) {
  const splitOnCode = useMemo(
    () => splitTextIntoSegments(removeAdjacentDuplicateMath(text)),
    [text],
  );

  const content = splitOnCode.map(({ type, content: segmentContent, language }, index) => {
    const key = `${type}-${index}`;

    if (type === 'code') {
      if (isMarkdownCodeLanguage(language)) {
        return (
          <MarkdownPreviewBlock
            key={key}
            content={segmentContent}
            onExpandMarkdown={onExpandMarkdown}
            linkComponent={renderLink}
          />
        );
      }

      if (isLatexCodeLanguage(language)) {
        return <KatexMath key={key} tex={segmentContent.trim()} display />;
      }

      return (
        <MarkdownCodeBlock
          key={key}
          content={segmentContent}
          language={language ?? 'python'}
          error={error}
          onExpandCode={onExpandCode}
        />
      );
    }

    return (
      <Typography key={key} component="div" sx={TEXT_SEGMENT_SX}>
        <RenderedMarkdownContent
          text={segmentContent}
          onExpandTable={onExpandTable}
          linkComponent={renderLink}
        />
      </Typography>
    );
  });

  return <div data-generatingmessage={`${isGeneratingMessage}`}>{content}</div>;
}

const RenderedMarkdownContent = memo(function RenderedMarkdownContent({
  text,
  onExpandTable,
  linkComponent = MarkdownLink,
}: {
  text: string;
  onExpandTable?: (table: MarkdownTableExpansion) => void;
  linkComponent?: MarkdownLinkComponent;
}) {
  // Parsing is pure over `text`; recompute only when the text changes, not on
  // unrelated parent re-renders (hover, feedback, etc.).
  const segments = useMemo(
    () => splitTextIntoMarkdownSegments(escapeIntrawordUnderscores(encodeMathAsTags(text))),
    [text],
  );

  // Keys only need to be stable within the current segment order. The type prefix
  // distinguishes a slot that flips text<->table as more content streams in, so the
  // index is sufficient — no need to stringify+hash the whole (potentially huge)
  // table on every streaming token, which also remounted the growing last segment.
  return segments.map((markdownSegment, segmentIndex) => {
    if (markdownSegment.type === 'table') {
      return (
        <MarkdownPipeTable
          // biome-ignore lint/suspicious/noArrayIndexKey: segments are append-only (streaming grows the last / appends new, never mid-inserts) and the type prefix distinguishes a text<->table flip, so the index is a stable key
          key={`table-${segmentIndex}`}
          header={markdownSegment.header}
          rows={markdownSegment.rows}
          onExpandTable={onExpandTable}
        />
      );
    }

    return (
      <Markdown
        // biome-ignore lint/suspicious/noArrayIndexKey: segments are append-only (streaming grows the last / appends new, never mid-inserts) and the type prefix distinguishes a text<->table flip, so the index is a stable key
        key={`text-${segmentIndex}`}
        children={markdownSegment.content}
        options={{
          forceBlock: true,
          overrides: {
            a: { component: linkComponent },
            [MATH_TAG]: { component: MarkdownMath },
            table: {
              component: MarkdownTable,
              props: { onExpandTable },
            },
          },
        }}
      />
    );
  });
});

// Renders the placeholder tags produced by encodeMathAsTags back into math via
// KaTeX. Display math gets its own scrollable line; inline math flows with text.
function MarkdownMath(props: { 'data-tex'?: string; 'data-display'?: string }) {
  return (
    <KatexMath
      tex={decodeURIComponent(props['data-tex'] ?? '')}
      display={props['data-display'] === 'true'}
    />
  );
}

function KatexMath({ tex, display = false }: { tex: string; display?: boolean }) {
  const html = useMemo(
    () => katex.renderToString(tex, { displayMode: display, output: 'html', throwOnError: false }),
    [tex, display],
  );

  return (
    <Box
      component="span"
      sx={display ? { display: 'block', my: 1, overflowX: 'auto' } : undefined}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: KaTeX renders TeX to sanitized HTML; this is the library's standard usage
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function MarkdownPreviewBlock({
  content,
  onExpandMarkdown,
  linkComponent,
}: {
  content: string;
  onExpandMarkdown?: (markdown: MarkdownPreviewExpansion) => void;
  linkComponent?: MarkdownLinkComponent;
}) {
  const [markdownCopied, setMarkdownCopied] = useState(false);

  return (
    <Box
      data-testid="agent-markdown-preview-artifact"
      sx={(theme) => ({
        my: 1,
        overflow: 'hidden',
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor:
          theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.035)' : 'rgba(17, 24, 39, 0.03)',
      })}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          minHeight: 36,
          px: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor:
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.035)'
              : 'rgba(17, 24, 39, 0.035)',
        })}
      >
        <Typography
          variant="caption"
          sx={{ flex: 1, color: 'text.secondary', fontSize: 12, fontWeight: 600 }}
        >
          Markdown preview
        </Typography>
        {onExpandMarkdown && (
          <MarkdownArtifactAction
            ariaLabel="Expand markdown"
            icon="expand"
            onClick={() => onExpandMarkdown({ title: 'Expanded markdown', content })}
          >
            Expand
          </MarkdownArtifactAction>
        )}
        <MarkdownArtifactAction
          ariaLabel="Copy markdown"
          icon={markdownCopied ? 'check' : 'copy'}
          onClick={() => {
            setMarkdownCopied(true);
            void window.navigator.clipboard?.writeText(content);
          }}
        >
          {markdownCopied ? 'Copied' : 'Copy'}
        </MarkdownArtifactAction>
      </Box>
      <MarkdownGuardedScrollBox
        contentTestId="agent-markdown-preview-scroll-region"
        contentSx={{
          maxHeight: 220,
          px: 1.5,
          py: 1.25,
          fontSize: 14,
          '& h1': {
            mt: 0,
            mb: 1,
            fontSize: 18,
          },
          '& h2, & h3': {
            mt: 0,
            mb: 1,
            fontSize: 16,
          },
          '& p': {
            mt: 0,
            mb: 1,
          },
          '& p:last-child': {
            mb: 0,
          },
          '& ul, & ol': {
            mt: 0,
            mb: 1,
            pl: 3,
          },
        }}
      >
        <RenderedMarkdownContent text={content} linkComponent={linkComponent} />
      </MarkdownGuardedScrollBox>
    </Box>
  );
}

function MarkdownPipeTable({
  header,
  rows,
  onExpandTable,
}: {
  header: Array<string>;
  rows: Array<Array<string>>;
  onExpandTable?: (table: MarkdownTableExpansion) => void;
}) {
  return (
    <ExpandableMarkdownTable onExpandTable={onExpandTable} source={{ header, rows }}>
      <PipeTableRows header={header} rows={rows} />
    </ExpandableMarkdownTable>
  );
}

function PipeTableRows({ header, rows }: Readonly<MarkdownPipeTableSource>) {
  const headerCells = header.map((cell, cellIndex) => ({
    key: stableStringHash(`header:${cellIndex}:${cell}`),
    value: cell,
  }));
  const bodyRows = rows.map((row, rowIndex) => ({
    cells: header.map((_, cellIndex) => ({
      key: stableStringHash(`cell:${rowIndex}:${cellIndex}:${row[cellIndex] ?? ''}`),
      value: row[cellIndex] ?? '',
    })),
    key: stableStringHash(`row:${rowIndex}:${row.join('|')}`),
  }));

  return (
    <>
      <thead>
        <tr>
          {headerCells.map((cell) => (
            <th key={cell.key}>
              <InlineMarkdownContent text={cell.value} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {bodyRows.map((row) => (
          <tr key={row.key}>
            {row.cells.map((cell) => (
              <td key={cell.key}>
                <InlineMarkdownContent text={cell.value} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </>
  );
}

const TABLE_STYLE = { borderCollapse: 'collapse', width: '100%' } as const;

/** A pipe table rebuilt from its parsed source, as its expand button shows it. */
export function PipeTable(source: Readonly<MarkdownPipeTableSource>) {
  return (
    <table style={TABLE_STYLE}>
      <PipeTableRows header={source.header} rows={source.rows} />
    </table>
  );
}

// One instance per table cell (dense tables have hundreds); memo so a parent
// re-render with unchanged cell text does not re-parse every cell.
const InlineMarkdownContent = memo(function InlineMarkdownContent({ text }: { text: string }) {
  return (
    <Markdown
      children={text}
      options={{
        forceInline: true,
        overrides: { a: { component: MarkdownLink }, [MATH_TAG]: { component: MarkdownMath } },
      }}
    />
  );
});

// Markdown links inherit a low-contrast default that is hard to read on the dark
// theme background, so render them through a theme-aware link color: a bright
// teal in dark mode and a strong blue in light mode, underlined either way.
const MARKDOWN_LINK_SX = (theme: Theme) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.info.dark,
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
  fontWeight: 500,
  '&:hover': {
    color: theme.palette.mode === 'dark' ? theme.palette.info.light : theme.palette.info.main,
  },
});

export function MarkdownLink({ children, ...props }: ComponentProps<'a'>) {
  return (
    <Link {...props} sx={MARKDOWN_LINK_SX}>
      {children}
    </Link>
  );
}

function MarkdownTable({
  children,
  onExpandTable,
  ...tableProps
}: ComponentProps<'table'> & {
  onExpandTable?: (table: MarkdownTableExpansion) => void;
}) {
  return (
    <ExpandableMarkdownTable onExpandTable={onExpandTable} tableProps={tableProps}>
      {children}
    </ExpandableMarkdownTable>
  );
}

function ExpandableMarkdownTable({
  children,
  onExpandTable,
  tableProps,
  source,
}: {
  children: ReactNode;
  onExpandTable?: (table: MarkdownTableExpansion) => void;
  tableProps?: ComponentProps<'table'>;
  source?: MarkdownPipeTableSource;
}) {
  const tableContent = (
    <table {...tableProps} style={TABLE_STYLE}>
      {children}
    </table>
  );

  return (
    <Box data-testid="agent-table-artifact" sx={TABLE_CONTAINER_SX}>
      {onExpandTable && (
        <IconButton
          aria-label="Expand table"
          className="agent-table-expand-btn"
          data-agent-table-expand
          size="small"
          disableFocusRipple
          onClick={() => onExpandTable({ title: 'Expanded table', content: tableContent, source })}
          sx={TABLE_EXPAND_BUTTON_SX}
        >
          <OpenInFullIcon sx={{ fontSize: 15 }} />
        </IconButton>
      )}
      <Box sx={TABLE_SCROLL_SX}>{tableContent}</Box>
    </Box>
  );
}

const TABLE_CONTAINER_SX = (theme: Theme) => ({
  position: 'relative' as const,
  my: 1,
  maxWidth: '100%',
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    minWidth: 'max-content',
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    px: 1,
    py: 0.75,
    textAlign: 'left',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
  },
  '& th': {
    bgcolor: 'action.hover',
    fontWeight: 700,
  },
  '&:hover .agent-table-expand-btn, &:focus-within .agent-table-expand-btn': {
    opacity: 1,
    pointerEvents: 'auto',
  },
});

const TABLE_EXPAND_BUTTON_SX = (theme: Theme) => ({
  position: 'absolute' as const,
  top: 6,
  right: 6,
  zIndex: 1,
  width: 26,
  height: 26,
  opacity: 0,
  pointerEvents: 'none' as const,
  bgcolor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 32, 0.78)' : 'rgba(255, 255, 255, 0.82)',
  transition: 'opacity 120ms ease',
  '&.Mui-focusVisible': {
    opacity: 1,
    pointerEvents: 'auto',
  },
  '&:hover': {
    bgcolor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 32, 0.92)' : 'rgba(255, 255, 255, 0.94)',
  },
});

const TABLE_SCROLL_SX = { maxWidth: '100%', overflowX: 'auto' } as const;

export default memo(MarkdownContent);
